import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import vlogs from "./bd"; // Importing the vlogs data
import CodeBlock from "./CodeBlock"; // Importing CodeBlock component
import AOS from "aos"; // Animate On Scroll library
import "aos/dist/aos.css"; // AOS styles

const Blog = () => {
  // Get the blog ID from URL parameters
  const { id } = useParams();

  // Find the specific vlog based on the ID
  const vlog = vlogs.find((blog) => blog.id === parseInt(id));

  // Initialize AOS for animations
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  // If vlog not found, return an error message
  if (!vlog) {
    return <div className="text-center text-red-500">Blog not found</div>;
  }

  // Format the blog's creation date
  const formattedDate = new Date(vlog.datecreated).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div data-aos="fade-up" className="mt-20 mb-20 mx-2">
      {/* Blog container */}
      <div className="relative w-full lg:w-9/12 mx-auto p-6 bg-white/30 backdrop-filter backdrop-blur-lg rounded-lg shadow-lg">
        {/* Thumbnail */}
        <img
          src={vlog.thumbnail}
          alt={vlog.title}
          className="w-full max-h-80 object-cover mb-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
        />

        {/* Blog content */}
        <div className="relative">
          {/* Blog Title */}
          <h1 className="mb-4 text-2xl lg:text-4xl font-bold text-gray-900">
            {vlog.title}
          </h1>

          {/* Blog Date */}
          <p className="text-sm text-gray-500 mb-6">{formattedDate}</p>

          {/* Blog Introduction */}
          <p className="mb-6 text-lg">{vlog.introduction}</p>

          {/* Section One */}
          {vlog.sectionone && (
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {vlog.sectiononeheading}
              </h2>
              <p className="text-gray-700">{vlog.sectionone}</p>
              {vlog.sonecode && (
                <CodeBlock
                  codeheading={vlog.sonecodeheading}
                  code={vlog.sonecode}
                />
              )}
            </section>
          )}

          {/* Section Two */}
          {vlog.sectiontwo && (
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {vlog.sectiontwoheading}
              </h2>
              <p className="text-gray-700">{vlog.sectiontwo}</p>
              {vlog.stwocode && (
                <CodeBlock
                  codeheading={vlog.stwocodeheading}
                  code={vlog.stwocode}
                />
              )}
            </section>
          )}

          {/* Section Three */}
          {vlog.sectionthree && (
            <section className="mb-12 flex flex-col lg:flex-row">
              <div className="flex-1 mr-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  {vlog.sectionthreeheading}
                </h2>
                <p
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: vlog.sectionthree }}
                ></p>
                {vlog.sthreecode && (
                  <CodeBlock
                    codeheading={vlog.sthreecodeheading}
                    code={vlog.sthreecode}
                  />
                )}
              </div>
              <div className="flex-1">
                <img
                  src={vlog.sthreeimage}
                  alt="Image description"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </section>
          )}

          {/* Section Four */}
          {vlog.sectionfour && (
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {vlog.sectionfourheading}
              </h2>
              <p className="text-gray-700">{vlog.sectionfour}</p>
              {vlog.sfourcode && (
                <CodeBlock
                  codeheading={vlog.sfourcodeheading}
                  code={vlog.sfourcode}
                />
              )}
            </section>
          )}

          {/* Section Five */}
          {vlog.sectionfive && (
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {vlog.sectionfiveheading}
              </h2>
              <p className="text-gray-700">{vlog.sectionfive}</p>
              {vlog.sfivecode && (
                <CodeBlock
                  codeheading={vlog.sfivecodeheading}
                  code={vlog.sfivecode}
                />
              )}
            </section>
          )}

          {/* Section Six */}
          {vlog.sectionsix && (
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                {vlog.sectionsixheading}
              </h2>
              <p className="text-gray-700">{vlog.sectionsix}</p>
              {vlog.ssixcode && (
                <CodeBlock
                  codeheading={vlog.ssixcodeheading}
                  code={vlog.ssixcode}
                />
              )}
            </section>
          )}

          {/* Section Seven */}
          {vlog.sectionseven && (
            <section className="mb-12 flex flex-col lg:flex-row">
              <div className="flex-1 p-4">
                <img
                  src={vlog.ssevenimage}
                  alt="Image description"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="flex-1 mr-4">
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  {vlog.sectionsevenheading}
                </h2>
                <p
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: vlog.sectionseven }}
                ></p>
                {vlog.ssevencode && (
                  <CodeBlock
                    codeheading={vlog.ssevencodeheading}
                    code={vlog.ssevencode}
                  />
                )}
              </div>
            </section>
          )}

          {/* Summary */}
          {vlog.summary && (
            <section className="mb-12">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                {vlog.summaryheading || "Summary"}
              </h3>
              <p
                className="text-gray-700"
                dangerouslySetInnerHTML={{ __html: vlog.summary }}
              ></p>
              {vlog.summarycode && (
                <CodeBlock
                  codeheading={vlog.summarycodeheading}
                  code={vlog.summarycode}
                />
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
